/**
 * Navigation: instrument search, recents, screeners.
 *
 * Two placements, one component. On desktop it is the static left rail and it
 * owns the search. On mobile it is an off-canvas drawer holding Recent and
 * Screeners only — the search moves to the sticky top bar, where it is
 * reachable without opening anything.
 */
import { useEffect, useRef } from "react";

import { InstrumentSearch } from "./InstrumentSearch.tsx";
import { displayNameFor, type InstrumentOption } from "../domain/instruments.ts";
import { SCREENERS, type ScreenerEntry } from "../fixtures/instruments.ts";

interface SidebarProps {
	activeCode: string;
	recents: readonly string[];
	activeScreener: string | null;
	onSelectInstrument: (code: string) => void;
	onSelectScreener: (slug: string | null) => void;
	catalogue?: readonly InstrumentOption[];
	catalogueOrigin?: "api" | "fixture";
	/** Live screener metadata when the worker answered, the bundled list otherwise. */
	screeners?: readonly ScreenerEntry[];
	screenersOrigin?: "api" | "fixture";
	/** `rail` is the desktop column; `drawer` is the mobile off-canvas panel. */
	variant?: "rail" | "drawer";
	/** Drawer only: close button handler. Selecting anything also closes. */
	onClose?: () => void;
	id?: string;
	/** Rail only: collapsed to a narrow strip, giving its width to the chart. */
	collapsed?: boolean;
	/** Rail only. Absent means the rail cannot be collapsed at all. */
	onToggleCollapsed?: () => void;
}

/** Groups come from the data, so the live catalogue can add one without a code change. */
function groupsOf(screeners: readonly ScreenerEntry[]): string[] {
	return [...new Set(screeners.map((entry) => entry.group))];
}

/**
 * Wrap a selection handler so choosing something also dismisses the drawer.
 *
 * Every item in the drawer navigates, and a drawer left open over the thing
 * it just navigated to is the most common mobile-nav bug. Composed in one
 * place so no handler can forget.
 */
export function dismissing<T>(select: (value: T) => void, onClose?: () => void): (value: T) => void {
	return (value: T) => {
		select(value);
		onClose?.();
	};
}

export function Sidebar({
	activeCode,
	recents,
	activeScreener,
	onSelectInstrument,
	onSelectScreener,
	catalogue,
	catalogueOrigin = "fixture",
	screeners = SCREENERS,
	screenersOrigin = "fixture",
	variant = "rail",
	onClose,
	id,
	collapsed = false,
	onToggleCollapsed,
}: SidebarProps) {
	const isDrawer = variant === "drawer";
	// Only the desktop rail collapses; the drawer is already a thing you open
	// and shut, and giving it a second way to half-close would be two controls
	// for one idea.
	const isCollapsed = !isDrawer && collapsed;
	const bodyId = `${id ?? "sidebar"}-body`;
	const closeRef = useRef<HTMLButtonElement>(null);

	// Opening the drawer moves focus into it, so the next Tab lands on the
	// navigation rather than back at the top of the page.
	useEffect(() => {
		if (isDrawer) closeRef.current?.focus();
	}, [isDrawer]);

	const select = dismissing(onSelectInstrument, onClose);
	const screen = dismissing(onSelectScreener, onClose);

	return (
		<nav
			id={id}
			className={isDrawer ? "sidebar sidebar--drawer" : "sidebar"}
			data-collapsed={isCollapsed ? "true" : undefined}
			aria-label="Instruments and screeners"
		>
			<div className="sidebar__brand">
				<span className="sidebar__mark" aria-hidden="true" />
				{isCollapsed ? null : <span className="sidebar__wordmark">quantist</span>}
				{isDrawer ? (
					<button ref={closeRef} type="button" className="sidebar__close" aria-label="Close navigation" onClick={onClose}>
						✕
					</button>
				) : null}
				{/* The body stays mounted and is hidden rather than unmounted, so
				    aria-controls always resolves and the collapsed column is a
				    real strip with a real control in it — never a dead 0px gap. */}
				{!isDrawer && onToggleCollapsed ? (
					<button
						type="button"
						className="panel-toggle sidebar__collapse"
						aria-expanded={!isCollapsed}
						aria-controls={bodyId}
						title={isCollapsed ? "Expand navigation" : "Collapse navigation"}
						onClick={onToggleCollapsed}
					>
						<span aria-hidden="true">{isCollapsed ? "»" : "«"}</span>
						<span className="visually-hidden">{isCollapsed ? "Expand navigation" : "Collapse navigation"}</span>
					</button>
				) : null}
			</div>

			<div id={bodyId} className="sidebar__body" hidden={isCollapsed}>
			{/* On mobile the search is in the top bar, always reachable. */}
			{isDrawer ? null : (
				<section className="sidebar__section">
					<InstrumentSearch onSelect={select} catalogue={catalogue} catalogueOrigin={catalogueOrigin} />
				</section>
			)}

			<section className="sidebar__section">
				<h2 className="sidebar__label">Recent</h2>
				<ul className="rail">
					{recents.map((code) => (
						<li key={code}>
							<button
								type="button"
								className="rail__item"
								aria-current={code === activeCode && !activeScreener ? "page" : undefined}
								onClick={() => select(code)}
							>
								<span className="rail__code">{code.toUpperCase()}</span>
								<span className="rail__meta">{displayNameFor(code)}</span>
							</button>
						</li>
					))}
				</ul>
			</section>

			<section className="sidebar__section sidebar__section--grow">
				<h2 className="sidebar__label">
					Screeners{screenersOrigin === "fixture" ? <span className="sidebar__offline"> · offline list</span> : null}
				</h2>
				{groupsOf(screeners).map((group) => (
					<div key={group} className="rail-group">
						<h3 className="rail-group__title">{group}</h3>
						<ul className="rail">
							{screeners.filter((entry: ScreenerEntry) => entry.group === group).map((entry) => (
								<li key={entry.slug}>
									<button
										type="button"
										className="rail__item rail__item--screener"
										aria-current={entry.slug === activeScreener ? "page" : undefined}
										onClick={() => screen(entry.slug === activeScreener ? null : entry.slug)}
									>
										<span className="rail__code rail__code--screener">{entry.label}</span>
										<span className="rail__meta">{entry.hint}</span>
									</button>
								</li>
							))}
						</ul>
					</div>
				))}
			</section>
			</div>
		</nav>
	);
}
